import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Activity, RefreshCw, CircleDot, ArrowUpRight, Zap } from "lucide-react";
import { toast } from "sonner";

// Phase 25.13v — Bloomberg-style near-live feed.
//
// The component:
//   1. Pings POST /api/live-feed/heartbeat every 30s while mounted.
//      This flags the workspace as "active", which the 5-min cron uses
//      to decide whether to pull fresh data from Xero/MYOB.
//   2. Polls GET /api/live-feed/recent-events every 10s, appending any
//      new events to a rolling list. Older events fade.
//   3. Renders a terminal-style ticker with kind-aware icons + amount.
//   4. Exposes a "FORCE SYNC" button (operator-triggered fast pull).
//
// "Near-live" framing rules:
//   - We avoid the word "real-time" — the refresh cycle is 5 minutes.
//   - The header pill clearly shows the data-freshness state.
//   - When new events arrive we briefly flash the row gold.

const ICONS = {
  sync: RefreshCw,
  invoice: ArrowUpRight,
  payment: Zap,
  new_customer: CircleDot,
  anomaly: Activity,
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function LiveFeed() {
  const [events, setEvents] = useState([]);
  const [serverTime, setServerTime] = useState(null);
  const [forcing, setForcing] = useState(false);
  const sinceRef = useRef(null);
  const containerRef = useRef(null);

  // Heartbeat — fires immediately on mount, then every 30s.
  useEffect(() => {
    let cancelled = false;
    const beat = async () => {
      try {
        await api.post("/live-feed/heartbeat");
      } catch {
        // Heartbeat is fire-and-forget; never bubble up.
      }
    };
    beat();
    const h = setInterval(() => { if (!cancelled) beat(); }, 30_000);
    return () => { cancelled = true; clearInterval(h); };
  }, []);

  // Recent events — polls every 10s, appends new rows.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const params = new URLSearchParams({ limit: 20 });
        if (sinceRef.current) params.set("since", sinceRef.current);
        const { data } = await api.get(`/live-feed/recent-events?${params.toString()}`);
        if (cancelled) return;
        setServerTime(data.server_time || null);
        if (data.events && data.events.length) {
          sinceRef.current = data.events[data.events.length - 1].created_at;
          setEvents(prev => [...data.events, ...prev].slice(0, 30));
        }
      } catch {
        // Network blip — quietly skip this tick.
      }
    };
    poll();
    const h = setInterval(poll, 10_000);
    return () => { cancelled = true; clearInterval(h); };
  }, []);

  const forceSync = async () => {
    setForcing(true);
    try {
      const { data } = await api.post("/live-feed/refresh-now", { source: "all" });
      const xeroN = data?.summary?.xero
        ? Object.values(data.summary.xero).reduce((a, b) => a + (Number(b) || 0), 0)
        : 0;
      const myobN = Number(data?.summary?.myob?.rows_added || 0);
      const total = xeroN + myobN;
      if (total > 0) toast.success(`Synced ${total} new row${total === 1 ? "" : "s"}`);
      else toast.message("Already up to date · no new rows since last sync");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setForcing(false);
    }
  };

  const newestAge = events.length > 0 ? timeAgo(events[0].created_at) : null;

  return (
    <section
      ref={containerRef}
      className="mt-8 border border-zinc-900 bg-[#040404]"
      data-testid="live-feed"
    >
      {/* Header */}
      <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-900 bg-[#050505] flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <span className="relative inline-flex">
            <span className="w-2 h-2 rounded-full bg-[#00E676] pulse-dot" />
            <span className="absolute inset-0 w-2 h-2 rounded-full bg-[#00E676] animate-ping opacity-50" />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000]">
            // LIVE FEED · DATA TICKER
          </span>
          <span className="font-mono text-[9px] text-zinc-600 hidden sm:inline">
            Xero · MYOB · Square · 5-min refresh{newestAge ? ` · latest event ${newestAge}` : ""}
          </span>
        </div>
        <button
          type="button"
          onClick={forceSync}
          disabled={forcing}
          data-testid="live-feed-force-sync"
          className="border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${forcing ? "animate-spin" : ""}`} />
          {forcing ? "SYNCING…" : "FORCE SYNC"}
        </button>
      </header>

      {/* Stream */}
      <div className="max-h-[320px] overflow-y-auto">
        {events.length === 0 && (
          <div className="px-5 py-10 text-center font-mono text-[11px] text-zinc-600 leading-relaxed">
            <div className="text-[#FFB000] mb-2">// AWAITING DATA</div>
            Connect Xero, MYOB, or Square and your feed will start ticking in within 5 minutes.
            <br />
            Or hit <span className="text-[#FFB000]">FORCE SYNC</span> for an immediate pull.
          </div>
        )}
        <ul className="divide-y divide-zinc-900">
          {events.map((ev, idx) => {
            const Icon = ICONS[ev.kind] || CircleDot;
            const flash = idx === 0 && ev.created_at && (Date.now() - new Date(ev.created_at).getTime()) < 15000;
            return (
              <li
                key={`${ev.created_at}-${idx}`}
                data-testid={`live-feed-event-${idx}`}
                className={`flex items-center gap-3 px-5 py-2.5 ${
                  flash ? "bg-[#1a1300]" : ""
                } transition-colors duration-300`}
              >
                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${flash ? "text-[#FFB000]" : "text-zinc-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] text-zinc-200 truncate">{ev.title}</div>
                  {ev.detail && (
                    <div className="font-mono text-[10px] text-zinc-600 truncate">{ev.detail}</div>
                  )}
                </div>
                {ev.source && (
                  <span className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000]/70 border border-[#FFB000]/30 bg-[#FFB000]/5 px-1.5 py-0.5 flex-shrink-0">
                    {ev.source}
                  </span>
                )}
                <span className="font-mono text-[10px] text-zinc-600 flex-shrink-0 min-w-[60px] text-right">
                  {timeAgo(ev.created_at)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Footer disclaimer */}
      <footer className="px-5 py-2 border-t border-zinc-900 bg-[#050505] flex items-center justify-between gap-2">
        <span className="font-mono text-[9px] text-zinc-700 uppercase tracking-widest">
          Refresh interval · 5 min · server time {serverTime ? new Date(serverTime).toLocaleTimeString() : "—"}
        </span>
        <span className="font-mono text-[9px] text-zinc-700 uppercase tracking-widest">
          ALL TIMES IN YOUR LOCAL TZ
        </span>
      </footer>
    </section>
  );
}
